import { useState } from 'react'
import { Trash2 } from 'lucide-react'
import { Button, EditableGrid, type EditableColumnDef } from '@/components/ui'
import { toast } from 'sonner'
import { useHazmatOverrides } from '../api/queries'
import type { HazmatOverride, Packet } from '../api/types'

export interface HazmatOverrideFormProps {
  packet: Packet
}

interface OverrideRow {
  /** Stable identity for the grid — the original product-id key for
   *  existing overrides, or a synthetic `new-N` token for a row the
   *  operator just added and hasn't picked a product id for yet. */
  rowKey: string
  productId: string
  /** Existing rows' product id is locked — the map key IS the identity,
   *  so changing it would silently orphan the old entry rather than
   *  rename it. Remove + re-add instead. */
  isExisting: boolean
  un_number: string
  hazmat_class_code: string
  packing_group: string
  proper_shipping_name: string
}

function rowsFromPacket(packet: Packet): OverrideRow[] {
  const overrides = packet.hazmat_manual_overrides ?? {}
  return Object.entries(overrides).map(([productId, o]) => ({
    rowKey: productId,
    productId,
    isExisting: true,
    un_number: o.un_number ?? '',
    hazmat_class_code: o.hazmat_class_code ?? '',
    packing_group: o.packing_group ?? '',
    proper_shipping_name: o.proper_shipping_name ?? '',
  }))
}

/**
 * DATA GAP (see module report): `PacketOut` carries only the raw
 * `hazmat_manual_overrides` map (product_id -> override fields) — no
 * product line detail (item code, description, qty) comes back from
 * either `GET /{packet_id}` or anywhere else in this API. That means
 * this form can't show "Product X, lot Y" the way a real product
 * picker would; it renders a plain numeric product-id text field for
 * new rows, which is honest about what data actually exists rather
 * than faking a picker experience the backend doesn't support yet.
 *
 * Only rendered by the parent when `packet.is_hazardous`. Every save
 * sends the FULL override map — the backend's PATCH is a full replace,
 * not a diff (`packet.hazmat_manual_overrides = body.overrides`).
 *
 * Callers must mount this with `key={packet.id}` (see
 * `PacketDetailPage`) so local edit state resets cleanly when the
 * operator navigates to a different packet, without needing an
 * internal effect that re-seeds state on every refetch of the SAME
 * packet (which would clobber in-progress edits).
 */
export function HazmatOverrideForm({ packet }: HazmatOverrideFormProps) {
  const [rows, setRows] = useState<OverrideRow[]>(() => rowsFromPacket(packet))
  const [nextNewId, setNextNewId] = useState(0)
  const mutation = useHazmatOverrides()

  const locked = packet.stage === 'finals_ready'

  const columns: EditableColumnDef<OverrideRow>[] = [
    {
      key: 'productId',
      header: 'Product ID',
      readonly: (row) => row.isExisting,
      width: '110px',
    },
    { key: 'un_number', header: 'UN Number', width: '110px' },
    { key: 'hazmat_class_code', header: 'Class Code', width: '110px' },
    { key: 'packing_group', header: 'Packing Group', width: '120px' },
    { key: 'proper_shipping_name', header: 'Proper Shipping Name' },
  ]

  function handleCellChange(rowIndex: number, key: keyof OverrideRow, value: unknown) {
    setRows((prev) => {
      const next = [...prev]
      const row = next[rowIndex]
      if (!row) return prev
      next[rowIndex] = { ...row, [key]: String(value ?? '') }
      return next
    })
  }

  function handleAddRow() {
    const rowKey = `new-${nextNewId}`
    setNextNewId((n) => n + 1)
    setRows((prev) => [
      ...prev,
      {
        rowKey,
        productId: '',
        isExisting: false,
        un_number: '',
        hazmat_class_code: '',
        packing_group: '',
        proper_shipping_name: '',
      },
    ])
  }

  function handleRemoveRow(rowKey: string) {
    setRows((prev) => prev.filter((r) => r.rowKey !== rowKey))
  }

  function handleSave() {
    const overrides: Record<string, HazmatOverride> = {}
    for (const row of rows) {
      const productId = row.productId.trim()
      if (!productId) continue
      if (!/^\d+$/.test(productId)) {
        toast.error(`"${productId}" isn't a valid numeric product id`)
        return
      }
      overrides[productId] = {
        un_number: row.un_number.trim() || null,
        hazmat_class_code: row.hazmat_class_code.trim() || null,
        packing_group: row.packing_group.trim() || null,
        proper_shipping_name: row.proper_shipping_name.trim() || null,
      }
    }
    mutation.mutate(
      { packetId: packet.id, overrides },
      {
        onSuccess: () => toast.success('Hazmat overrides saved'),
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'save failed'
          toast.error(msg, { duration: 8000 })
        },
      },
    )
  }

  return (
    <section className="flex flex-col gap-3 rounded-md border border-border-default bg-surface p-4">
      <header>
        <h3 className="text-role-section-label m-0">Hazmat Overrides</h3>
        <p className="m-0 mt-1 text-xs text-tertiary">
          This shipment has hazardous lines. Use overrides only for products where Odoo&apos;s
          hazmat data (UN number / class code / proper shipping name) is genuinely incomplete —
          the server rejects an override for any product whose Odoo data is already complete.
          Enter the raw numeric product id; there is no product picker wired up here yet.
        </p>
      </header>

      <EditableGrid<OverrideRow>
        rows={rows}
        columns={columns}
        onCellChange={handleCellChange}
        onAddRow={locked ? undefined : handleAddRow}
        addRowLabel="Add override"
        rowActions={
          locked
            ? undefined
            : (row) => (
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={`Remove override for product ${row.productId || '(new)'}`}
                  onClick={() => handleRemoveRow(row.rowKey)}
                >
                  <Trash2 size={14} />
                </Button>
              )
        }
        minTableWidth="700px"
        maxHeight="40vh"
      />

      <div className="flex items-center justify-end gap-2">
        {locked ? (
          <span className="text-role-metadata text-tertiary">
            Overrides are locked once finals are ready.
          </span>
        ) : (
          <Button variant="primary" size="sm" loading={mutation.isPending} onClick={handleSave}>
            Save overrides
          </Button>
        )}
      </div>
    </section>
  )
}
